import * as React from "react";
import Select from 'react-select';
import {none, option, Option} from "ts-option";
import {api} from "../../../api/api";
import AddressSummary from "../../../model/address/AddressSummary";
import Toast from "../../helper/Toast";
import DictionaryCache from "../../../helper/DictionaryCache";
import AddressesWithGroups from "../../../model/address/AddressesWithGroups";
import Address from "../../../model/address/Address";
import {FocusableProps} from "../../common/props/FocusableProps";
import {PlaceholderProps} from "../../common/props/PlaceholderProps";
import {ActionMeta, InputActionMeta} from "react-select/lib/types";
import {SelectComponentDefaultValue} from "../../ui/select/SelectComponent";
import AddressApi from "../../../api/AddressApi";
import {isPhoneScreen} from "../../../utils/Utils";

const uuidv4 = require('uuid/v4');

interface ComponentProps {
	onSelect: (address: Address) => void;
	visible: boolean;
	initialSearchQuery?: Option<string>;
	addressApi?: AddressApi;
}

type Props = ComponentProps & FocusableProps & PlaceholderProps

interface State {
	queryText: string;
	addresses: AddressSummary[];
	loading: boolean;
	lastRequestedQuery: Option<string>;
	sessionToken: string;
}

const minCharsToSearch = 4;

/**
 * How long typing has to pause before a lookup is sent. Every lookup is billed by the address provider,
 * so firing one per keystroke turns a single address into a dozen paid requests.
 */
const searchDebounceMs = 350;

export default class AddressSelectionComponent extends React.PureComponent<Props, State> {
	public static defaultProps: Partial<Props> = {
		initialSearchQuery: none
	};

	refs: {
		select: Select<AddressSummary>;
	};

	cache: DictionaryCache<AddressesWithGroups>;

	private searchTimeout: any;

	constructor(props: Props) {
		super(props);
		this.onInputChange = this.onInputChange.bind(this);
		this.onSelected = this.onSelected.bind(this);
		this.getNoResultsText = this.getNoResultsText.bind(this);
		this.getOptionLabel = this.getOptionLabel.bind(this);
		this.cache = DictionaryCache.empty();
		const initialQueryText = this.props.initialSearchQuery ? this.props.initialSearchQuery.getOrElse("") : "";
		this.state = {
			queryText: initialQueryText,
			addresses: [],
			loading: false,
			lastRequestedQuery: none,
			sessionToken: uuidv4()
		};
	}

	componentDidUpdate(prevProps: Props) {
		if (this.refs.select && prevProps &&
			((prevProps.focus == false && this.props.focus == true) || (!prevProps.visible && this.props.visible))) {
			this.refs.select.focus();
			this.state.queryText.length && this.findByAddress(this.state.queryText);
		}
	}

	componentDidMount(): void {
		if (this.refs.select && this.props.focus && !isPhoneScreen()) {
			this.refs.select.focus();
		}
		if (this.props.focus && this.state.queryText.length) {
			this.findByAddress(this.state.queryText);
		}
	}

	componentWillUnmount(): void {
		clearTimeout(this.searchTimeout);
	}

	render() {
		const value = this.state.queryText.length ?
			new SelectComponentDefaultValue(this.state.queryText, this.state.queryText) : null;
		return (
			<Select
				ref="select"
				classNamePrefix={"c-select-component"}
				inputValue={this.state.queryText}
				value={value}
				styles={{container: (base) => ({...base, display: this.props.visible ? '' : 'none'})}}
				placeholder={this.props.placeholder}
				noOptionsMessage={this.getNoResultsText}
				filterOption={(element: any, filter: string) => true}
				options={this.state.addresses}
				isLoading={this.state.loading}
				isClearable={true}
				onInputChange={this.onInputChange}
				onChange={this.onSelected}
				onFocus={this.props.onFocus}
				getOptionLabel={this.getOptionLabel}
				closeMenuOnSelect={false}
				openMenuOnFocus={true}/>
		)
	}

	getOptionLabel(props: AddressSummary | SelectComponentDefaultValue) {
		if (props instanceof AddressSummary) {
			return `${props.text} ${props.description}`;
		}
		return props.label;
	}

	getNoResultsText(): string {
		if (this.state.loading)
			return "Loading ...";
		else if (this.state.queryText.length >= minCharsToSearch)
			return "No results found";
		else
			return "Type to search";
	}

	onInputChange(searchText: string, inputActionMeta: InputActionMeta) {
		if (inputActionMeta.action === 'input-change') {
			this.setState({queryText: searchText});
			if (searchText.length >= minCharsToSearch) {
				const cachedData = this.cache.get(searchText);
				if (cachedData.isDefined)
					this.setState({
						addresses: cachedData.get.addresses,
						loading: false,
						lastRequestedQuery: option(searchText)
					});
				else
					this.scheduleSearch(searchText);
			} else {
				clearTimeout(this.searchTimeout);
				this.setState({addresses: [], loading: false});
			}
		}
	}

	onSelected(selected: AddressSummary | null, actionMeta: ActionMeta) {
		if (actionMeta.action === 'clear') {
			this.cache = DictionaryCache.empty();
			this.setState({queryText: "", addresses: [], sessionToken: uuidv4()});
		} else if (selected instanceof AddressSummary) {
			this.setState({loading: true}, () => {
				this.getAddressApi().getFullAddress(selected.id, this.state.sessionToken).then(address => {
					this.setState({loading: false}, () => {
						this.props.onSelect(address);
					});
				}).catch((err) => {
					this.setState({loading: false});
					const status = err && err.response && err.response.status;
					const exception = err && err.response && err.response.data && err.response.data.exception;
					if (status === 404) {
						Toast.error("Address not found, please pick another");
					} else if (status === 400 && exception === "AddressMissingPostcodeException") {
						Toast.error("Selected address has no postcode, please pick another");
					} else if (status === 429) {
						Toast.error("Too many address searches. Please try again later");
					} else {
						Toast.error("Error on fetching the address details");
					}
				});
			});
		}
	}

	/**
	 * Shows the spinner straight away but sends the lookup only once typing pauses, so a whole address
	 * costs one request instead of one per character.
	 */
	private scheduleSearch(searchText: string) {
		clearTimeout(this.searchTimeout);
		this.setState({loading: true});
		this.searchTimeout = setTimeout(() => this.findByAddress(searchText), searchDebounceMs);
	}

	private findByAddress(searchText: string) {
		this.setState({loading: true, lastRequestedQuery: option(searchText)}, () => {
			this.getAddressApi().findAddressesByText(searchText, this.state.sessionToken).then(result => {
				this.cache.store(searchText, result);
				this.setState({addresses: result.addresses, loading: false});
			}).catch((err) => {
				this.setState({addresses: [], loading: false});
				const status = err && err.response && err.response.status;
				Toast.error(status === 429
					? "Too many address searches. Please try again later"
					: "Error on searching addresses");
			});
		});
	}

	public resetSearchInput() {
		this.cache = DictionaryCache.empty();
		this.setState({queryText: "", sessionToken: uuidv4()});
	}

	private getAddressApi(): AddressApi {
		return this.props.addressApi || api.instance.addressApi;
	}
}
